import React, { useState, useMemo, useRef, useEffect } from 'react';
import './PracticesPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
];

const EMPTY_FORM = {
  name: '', npi: '', address1: '', city: '', state: '', zip: '',
  phone: '', tax_id: '',
  clearinghouse: '', clearinghouse_other: '', clearinghouse_credentials: {},
  emr_system: '', emr_other: '', emr_credentials: {},
  pm_system: '', pm_other: '', pm_credentials: {},
  providers: [],
};

const EMPTY_PROVIDER = { first_name: '', last_name: '', npi: '', provider_type: '' };

const PROVIDER_TYPES = [
  'MD', 'NP', 'APRN', 'PA', 'PT', 'OT', 'DC',
  'SLP', 'LCSW', 'Psychologist', 'RD', 'DPM', 'Other',
];

// Dropdown options. Keys marked with hasApi trigger credential-field prompts.
const INTEGRATION_OPTIONS = {
  clearinghouse: [
    { value: 'waystar', label: 'Waystar' },
    { value: 'change', label: 'Change' },
    { value: 'trizetto', label: 'TriZetto' },
    { value: 'officeally', label: 'Office Ally' },
    { value: 'claimmd', label: 'Claim.MD', hasApi: true },
    { value: 'stedi', label: 'Stedi', hasApi: true },
    { value: 'other', label: 'Other' },
  ],
  emr: [
    { value: 'athena', label: 'Athena' },
    { value: 'ecw', label: 'eClinicalWorks', hasApi: true },
    { value: 'tebra', label: 'Tebra', hasApi: true },
    { value: 'modmed', label: 'ModMed' },
    { value: 'other', label: 'Other' },
  ],
  pm: [
    { value: 'practicesuite', label: 'PracticeSuite', hasApi: true },
    { value: 'openpm', label: 'OpenPM' },
    { value: 'advancedmd', label: 'AdvancedMD' },
    { value: 'other', label: 'Other' },
  ],
};

function hasApiIntegration(group, value) {
  return INTEGRATION_OPTIONS[group].some(o => o.value === value && o.hasApi);
}

function formatTaxId(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 2) return digits.slice(0, 2) + '-' + digits.slice(2);
  return digits;
}

function formatZip(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 5) return digits.slice(0, 5) + '-' + digits.slice(5);
  return digits;
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length >= 7) return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
  if (digits.length >= 4) return digits.slice(0, 3) + '-' + digits.slice(3);
  return digits;
}

function validateNpi(npi) {
  if (!npi) return null;
  const digits = npi.replace(/\D/g, '');
  if (digits.length !== 10) return 'NPI must be exactly 10 digits';
  // Luhn check with CMS-mandated 80840 prefix
  const full = '80840' + digits;
  let sum = 0;
  for (let i = full.length - 1; i >= 0; i--) {
    let d = parseInt(full[i], 10);
    if ((full.length - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  if (sum % 10 !== 0) return 'Invalid NPI (failed check digit)';
  return null;
}

function PracticesPage({ practices, onSelectPractice, onPracticesUpdated, onGoToAdmin,
                         onGoToMissionControl, userRole }) {
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isSuperAdmin = userRole === 'super_admin';
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [step, setStep] = useState(1);
  const [credSchemas, setCredSchemas] = useState({ clearinghouse: null, emr: null, pm: null });
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInputRef = useRef(null);

  const groupToSystemField = { clearinghouse: 'clearinghouse', emr: 'emr_system', pm: 'pm_system' };
  const groupToCredsField = { clearinghouse: 'clearinghouse_credentials', emr: 'emr_credentials', pm: 'pm_credentials' };

  const handleIntegrationChange = async (group, value) => {
    const systemField = groupToSystemField[group];
    const credsField = groupToCredsField[group];
    setForm(f => ({ ...f, [systemField]: value, [credsField]: {} }));
    setCredSchemas(s => ({ ...s, [group]: null }));
    if (hasApiIntegration(group, value)) {
      try {
        const response = await fetch(`${API_URL}/api/credential-schema/${value}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        setCredSchemas(s => ({ ...s, [group]: data.fields }));
      } catch (err) {
        setError(`Could not load credential fields for ${value}. Refresh and try again.`);
      }
    }
  };

  const handleCredFieldChange = (group, key, value) => {
    const credsField = groupToCredsField[group];
    setForm(f => ({ ...f, [credsField]: { ...f[credsField], [key]: value } }));
  };

  const addProviderRow = () => {
    setForm(f => ({ ...f, providers: [...f.providers, { ...EMPTY_PROVIDER }] }));
  };

  const removeProviderRow = (index) => {
    setForm(f => ({ ...f, providers: f.providers.filter((_, i) => i !== index) }));
  };

  const updateProviderRow = (index, key, value) => {
    setForm(f => ({
      ...f,
      providers: f.providers.map((p, i) => (i === index ? { ...p, [key]: value } : p)),
    }));
  };

  const useSearchUI = practices.length >= 2;

  useEffect(() => {
    if (useSearchUI && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [useSearchUI]);

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return practices.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [trimmedQuery, practices]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleSearchKeyDown = (e) => {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSelectPractice(filtered[activeIndex]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'npi') setForm({ ...form, npi: value.replace(/\D/g, '').slice(0, 10) });
    else if (name === 'tax_id') setForm({ ...form, tax_id: formatTaxId(value) });
    else if (name === 'zip') setForm({ ...form, zip: formatZip(value) });
    else if (name === 'phone') setForm({ ...form, phone: formatPhone(value) });
    else setForm({ ...form, [name]: value });
  };

  const validateBasicInfo = () => {
    if (!form.name.trim()) return 'Practice name is required';
    if (!form.address1.trim()) return 'Address is required';
    if (!form.city.trim()) return 'City is required';
    if (!form.state) return 'State is required';
    const zipDigits = form.zip.replace(/\D/g, '');
    if (!zipDigits || (zipDigits.length !== 5 && zipDigits.length !== 9)) return 'ZIP must be 5 or 9 digits';
    const npiErr = validateNpi(form.npi);
    if (npiErr) return `Organization NPI: ${npiErr}`;
    if (form.tax_id && form.tax_id.replace(/\D/g, '').length !== 9) return 'Tax ID must be 9 digits';
    return null;
  };

  const validateProviders = () => {
    for (let i = 0; i < form.providers.length; i++) {
      const p = form.providers[i];
      const err = validateNpi(p.npi);
      if (err) return `Provider ${i + 1} NPI: ${err}`;
    }
    return null;
  };

  // Catches the bug where a typed API key disappeared because the dropdown
  // wasn't on a hasApi value — or where the credential field never rendered
  // because the schema fetch failed silently.
  const validateCredentials = () => {
    const groups = [
      { group: 'clearinghouse', systemField: 'clearinghouse', credsField: 'clearinghouse_credentials' },
      { group: 'emr', systemField: 'emr_system', credsField: 'emr_credentials' },
      { group: 'pm', systemField: 'pm_system', credsField: 'pm_credentials' },
    ];
    for (const { group, systemField, credsField } of groups) {
      const selected = form[systemField];
      if (!hasApiIntegration(group, selected)) continue;
      const label = INTEGRATION_OPTIONS[group].find(o => o.value === selected)?.label || selected;
      const schema = credSchemas[group];
      if (!schema) {
        return `${label} credential fields didn't load. Re-select the integration to try again.`;
      }
      const creds = form[credsField] || {};
      const missing = schema
        .filter(f => f.required && !(creds[f.key] || '').trim())
        .map(f => f.label);
      if (missing.length) {
        return `${label} requires: ${missing.join(', ')}.`;
      }
    }
    return null;
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setError(null);
    const err = validateBasicInfo();
    if (err) { setError(err); return; }
    setStep(2);
  };

  const handleStep2Next = (e) => {
    e.preventDefault();
    setError(null);
    const credErr = validateCredentials();
    if (credErr) { setError(credErr); return; }
    setStep(3);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setStep(1);
    setError(null);
    setForm({ ...EMPTY_FORM });
    setCredSchemas({ clearinghouse: null, emr: null, pm: null });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    const basicErr = validateBasicInfo();
    if (basicErr) { setError(basicErr); setStep(1); return; }
    const credErr = validateCredentials();
    if (credErr) { setError(credErr); setStep(2); return; }
    const providerErr = validateProviders();
    if (providerErr) { setError(providerErr); return; }

    const resolveSystem = (sel, other) => {
      if (!sel) return null;
      if (sel === 'other') return (other || '').trim() || null;
      return sel;
    };
    const clearinghouse = resolveSystem(form.clearinghouse, form.clearinghouse_other);
    const emrSystem = resolveSystem(form.emr_system, form.emr_other);
    const pmSystem = resolveSystem(form.pm_system, form.pm_other);

    const hasAnyValue = (obj) => Object.values(obj || {}).some(v => (v || '').toString().trim());

    const payload = {
      name: form.name, npi: form.npi, address1: form.address1, city: form.city,
      state: form.state, zip: form.zip, phone: form.phone, tax_id: form.tax_id,
      clearinghouse, emr_system: emrSystem, pm_system: pmSystem,
    };
    if (hasApiIntegration('clearinghouse', form.clearinghouse) && hasAnyValue(form.clearinghouse_credentials)) {
      payload.clearinghouse_credentials = form.clearinghouse_credentials;
    }
    if (hasApiIntegration('emr', form.emr_system) && hasAnyValue(form.emr_credentials)) {
      payload.emr_credentials = form.emr_credentials;
    }
    if (hasApiIntegration('pm', form.pm_system) && hasAnyValue(form.pm_credentials)) {
      payload.pm_credentials = form.pm_credentials;
    }

    setCreating(true);

    try {
      const response = await fetch(`${API_URL}/api/practices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        // POST each provider that has at least a name or NPI
        const providers = form.providers.filter(p =>
          (p.first_name || '').trim() || (p.last_name || '').trim() || (p.npi || '').trim()
        );
        for (const prov of providers) {
          try {
            await fetch(`${API_URL}/api/practices/${data.id}/providers`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                first_name: prov.first_name || null,
                last_name: prov.last_name || null,
                npi: prov.npi || null,
                provider_type: prov.provider_type || null,
              }),
            });
          } catch {
            // Individual provider failure shouldn't block — surface later
          }
        }
        // Fire-and-forget initial sync if an integrated clearinghouse was set
        // up — saves the user a manual click after onboarding.
        if (['claimmd', 'stedi'].includes(data.clearinghouse) && data.has_clearinghouse_credentials) {
          fetch(`${API_URL}/api/practices/${data.id}/clearinghouse/sync`, {
            method: 'POST',
            credentials: 'include',
          }).catch(() => { /* user will see status on the practice page */ });
        }
        closeCreate();
        if (onPracticesUpdated) onPracticesUpdated();
        onSelectPractice(data);
      } else {
        setError(data.error || 'Failed to create practice');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const createModal = showCreate && (
    <div className="modal-overlay" onClick={closeCreate}>
      <div className="create-modal" onClick={(e) => e.stopPropagation()}>
        <div className="create-modal-header">
          <h2>New Practice</h2>
        </div>
        <div className="create-modal-steps">
          <span className={`step-dot${step === 1 ? ' active' : ''}`}>1</span>
          <span className="step-sep" />
          <span className={`step-dot${step === 2 ? ' active' : ''}`}>2</span>
          <span className="step-sep" />
          <span className={`step-dot${step === 3 ? ' active' : ''}`}>3</span>
        </div>
        <div className="create-modal-substep">
          {step === 1 && 'Step 1 of 3 — Practice info'}
          {step === 2 && 'Step 2 of 3 — Integrations (optional)'}
          {step === 3 && 'Step 3 of 3 — Providers (optional)'}
        </div>
        {error && <div className="modal-error">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleNextStep}>
            <div className="form-row">
              <div className="form-field full">
                <label>Practice Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="e.g., Sunrise Physical Therapy" required autoFocus />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Organization NPI</label>
                <input name="npi" value={form.npi} onChange={handleChange} placeholder="10 digits" maxLength={10} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field full">
                <label>Address *</label>
                <input name="address1" value={form.address1} onChange={handleChange} placeholder="Street address" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>City *</label>
                <input name="city" value={form.city} onChange={handleChange} required />
              </div>
              <div className="form-field small">
                <label>State *</label>
                <select name="state" value={form.state} onChange={handleChange} required>
                  <option value="">--</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field small">
                <label>ZIP *</label>
                <input name="zip" value={form.zip} onChange={handleChange} placeholder="12345" maxLength={10} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="XXX-XXX-XXXX" maxLength={12} />
              </div>
              <div className="form-field">
                <label>Tax ID</label>
                <input name="tax_id" value={form.tax_id} onChange={handleChange} placeholder="XX-XXXXXXX (optional)" maxLength={10} />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeCreate}>Cancel</button>
              <button type="submit" className="btn btn-primary">Next</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleStep2Next}>
            {[
              { group: 'clearinghouse', label: 'Clearinghouse', systemField: 'clearinghouse', otherField: 'clearinghouse_other', credsField: 'clearinghouse_credentials' },
              { group: 'emr', label: 'EMR', systemField: 'emr_system', otherField: 'emr_other', credsField: 'emr_credentials' },
              { group: 'pm', label: 'Practice Management', systemField: 'pm_system', otherField: 'pm_other', credsField: 'pm_credentials' },
            ].map((row) => {
              const selected = form[row.systemField];
              const schema = credSchemas[row.group];
              return (
                <div key={row.group} className="integration-row">
                  <div className="form-row">
                    <div className="form-field">
                      <label>{row.label}</label>
                      <select
                        value={selected}
                        onChange={(e) => handleIntegrationChange(row.group, e.target.value)}
                      >
                        <option value="">None</option>
                        {INTEGRATION_OPTIONS[row.group].map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {selected === 'other' && (
                      <div className="form-field">
                        <label>Name</label>
                        <input
                          value={form[row.otherField]}
                          onChange={(e) => setForm(f => ({ ...f, [row.otherField]: e.target.value }))}
                          placeholder="Enter name"
                        />
                      </div>
                    )}
                  </div>
                  {hasApiIntegration(row.group, selected) && schema && schema.map(field => (
                    <div key={field.key} className="form-row">
                      <div className="form-field full">
                        <label>{field.label}{field.required ? ' *' : ''}</label>
                        <input
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={form[row.credsField][field.key] || ''}
                          onChange={(e) => handleCredFieldChange(row.group, field.key, e.target.value)}
                          placeholder={field.type === 'password' ? 'Paste key' : ''}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button type="submit" className="btn btn-primary">Next</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleCreate}>
            {form.providers.length === 0 ? (
              <div className="providers-empty">
                No providers yet — add them now or later from the practice page.
              </div>
            ) : (
              form.providers.map((prov, idx) => (
                <div key={idx} className="provider-row">
                  <div className="provider-row-header">
                    <span className="provider-row-label">Provider {idx + 1}</span>
                    <button
                      type="button"
                      className="provider-row-remove"
                      onClick={() => removeProviderRow(idx)}
                      aria-label="Remove provider"
                    >×</button>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label>First Name</label>
                      <input
                        value={prov.first_name}
                        onChange={(e) => updateProviderRow(idx, 'first_name', e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>Last Name</label>
                      <input
                        value={prov.last_name}
                        onChange={(e) => updateProviderRow(idx, 'last_name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label>NPI</label>
                      <input
                        value={prov.npi}
                        onChange={(e) => updateProviderRow(idx, 'npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10 digits"
                        maxLength={10}
                      />
                    </div>
                    <div className="form-field">
                      <label>Type</label>
                      <select
                        value={prov.provider_type}
                        onChange={(e) => updateProviderRow(idx, 'provider_type', e.target.value)}
                      >
                        <option value="">--</option>
                        {PROVIDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))
            )}

            <button type="button" className="btn btn-add-provider" onClick={addProviderRow}>
              + Add Provider
            </button>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Creating...' : 'Create Practice'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (useSearchUI) {
    return (
      <div className="practices-page practices-search-page">
        <div className="practices-search-container">
          <img src="/logo.png" alt="Atlas Billers" className="practices-search-logo" />
          <div className="practices-search-box">
            <svg className="practices-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="practices-search-input"
              placeholder="Search practices"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                className="practices-search-clear"
                onClick={() => { setQuery(''); searchInputRef.current?.focus(); }}
                aria-label="Clear search"
              >×</button>
            )}
          </div>

          {trimmedQuery && (
          <div className="practices-search-results">
            {filtered.length === 0 ? (
              <div className="practices-search-empty">No practices match "{query}"</div>
            ) : (
              filtered.map((p, idx) => (
                <div
                  key={p.id}
                  className={`practices-search-result${idx === activeIndex ? ' active' : ''}`}
                  onClick={() => onSelectPractice(p)}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <div className="practices-search-result-name">{p.name}</div>
                  <div className="practices-search-result-meta">
                    {p.npi && <span className="practices-search-result-npi">NPI: {p.npi}</span>}
                    {(p.city || p.state) && (
                      <span className="practices-search-result-loc">
                        {[p.city, p.state].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          )}
        </div>

        {isAdmin && (
          <div className="practices-fab-stack">
            {onGoToAdmin && (
              <button
                type="button"
                className="practices-fab practices-fab-secondary"
                onClick={onGoToAdmin}
                aria-label="Open admin page"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Admin</span>
              </button>
            )}
            {/* BELOW Admin, per David 2026-08-10. The stack is flex-direction: column,
                so source order is visual order. super_admin only, matching the sidebar
                entry and the backend's _MINIMUM_ROLE. */}
            {isSuperAdmin && onGoToMissionControl && (
              <button
                type="button"
                className="practices-fab practices-fab-secondary"
                onClick={onGoToMissionControl}
                aria-label="Open Mission Control"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="9" />
                  <rect x="14" y="3" width="7" height="5" />
                  <rect x="14" y="12" width="7" height="9" />
                  <rect x="3" y="16" width="7" height="5" />
                </svg>
                <span>Mission Control</span>
              </button>
            )}
            <button
              type="button"
              className="practices-fab"
              onClick={() => setShowCreate(true)}
              aria-label="Add new practice"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add New Practice</span>
            </button>
          </div>
        )}

        {createModal}
      </div>
    );
  }

  return (
    <div className="practices-page">
      <div className="practices-container">
        <div className="practices-header">
          <div>
            <h1>Atlas</h1>
            <p>Select a practice to get started</p>
          </div>
        </div>

        <div className="practices-grid">
          {practices.map((p) => (
            <div key={p.id} className="practice-card" onClick={() => onSelectPractice(p)}>
              <div className="practice-card-header">
                <h3>{p.name}</h3>
                {p.npi && <span className="practice-npi">NPI: {p.npi}</span>}
              </div>
            </div>
          ))}

          {isAdmin && (
            <div className="practice-card add-card" onClick={() => setShowCreate(true)}>
              <div className="add-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
              </div>
              <span>Add New Practice</span>
            </div>
          )}
        </div>

        {isAdmin && (onGoToAdmin || (isSuperAdmin && onGoToMissionControl)) && (
          <div className="practices-fab-stack">
            {onGoToAdmin && (
            <button
              type="button"
              className="practices-fab practices-fab-secondary"
              onClick={onGoToAdmin}
              aria-label="Open admin page"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Admin</span>
            </button>
            )}
          {/* Same pairing on this layout branch. It was missing entirely, so the button
              disappeared whenever this stack rendered instead of the one above. */}
          {isSuperAdmin && onGoToMissionControl && (
            <button
              type="button"
              className="practices-fab practices-fab-secondary"
              onClick={onGoToMissionControl}
              aria-label="Open Mission Control"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="9" />
                <rect x="14" y="3" width="7" height="5" />
                <rect x="14" y="12" width="7" height="9" />
                <rect x="3" y="16" width="7" height="5" />
              </svg>
              <span>Mission Control</span>
            </button>
          )}
          </div>
        )}

        {createModal}
      </div>
    </div>
  );
}

export default PracticesPage;
